// Hero-variants comparison page — produced for design review on 2026-02-10.
// Renders all 3 design-agent proposals stacked vertically so the founder can
// pick a direction. Each section is wrapped in its own "card" with a label so
// it's obvious which is which when screenshotted.
//
// Access: /mockups/hero-variants
import React from "react";

const SUN = "#FF8A5C";
const INK = "#0B2440";

// ===========================================================================
// SHARED HEADLINE — same copy across all 3 so the only visual difference is
// the right-side composition + the pill.
// ===========================================================================
function HeroFrame({ children, label }) {
  return (
    <div className="border-t border-[var(--srs-line)] bg-[var(--srs-bg)]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-[10px] tracking-[0.3em] text-[var(--srs-ink-faint)] mb-4 font-bold uppercase">
          {label}
        </div>
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-4">// EUROPILOT</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
              Tjen <span style={{ color: SUN, fontStyle: "italic", fontWeight: 500 }}>SAS&nbsp;poeng</span><br />
              og Trumf-bonus<br />
              <span className="text-[var(--srs-ink-dim)]">på hver handel.</span>
            </h1>
            <p className="mt-6 text-[var(--srs-ink-dim)] text-base max-w-md leading-relaxed">
              EuroPilot oppdager automatisk alle bonusprogrammer på nettsteder du allerede handler hos.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-[var(--srs-ink)] text-[var(--srs-bg)] text-[11px] tracking-[0.25em] font-bold uppercase">
              Start gratis prøveperiode
            </div>
          </div>
          <div className="relative min-h-[400px]">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// DIRECTION 1 — "Editorial Boarding Pass"
// Single typeset artifact (boarding-pass shape). Pill = glistening edge sweep.
// ===========================================================================
function Direction1() {
  return (
    <HeroFrame label="Direction 1 — The Editorial Boarding Pass">
      {/* Boarding-pass artifact */}
      <div className="relative bg-[var(--srs-surface)] border border-[var(--srs-line)]" style={{ borderRadius: "2px" }}>
        {/* Perforation between the two halves */}
        <div className="absolute left-[64%] top-4 bottom-4 border-l border-dashed border-[var(--srs-line)]" />
        <div className="grid grid-cols-[1fr_auto] gap-8 p-8">
          <div>
            <div className="text-[9px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-4">BOARDING PASS · EUROPILOT</div>
            <div className="text-[10px] tracking-[0.25em] text-[var(--srs-ink-faint)] mb-1">PASSENGER</div>
            <div className="font-display text-2xl font-bold mb-5">Simen Skog</div>
            <div className="flex items-baseline gap-4 mb-5">
              <div>
                <div className="text-[10px] tracking-[0.25em] text-[var(--srs-ink-faint)] mb-1">FROM</div>
                <div className="font-display text-3xl font-bold">KMP</div>
                <div className="text-xs text-[var(--srs-ink-dim)]">komplett.no</div>
              </div>
              <div className="text-[var(--srs-ink-faint)] text-2xl px-2">→</div>
              <div>
                <div className="text-[10px] tracking-[0.25em] text-[var(--srs-ink-faint)] mb-1">TO</div>
                <div className="font-display text-3xl font-bold" style={{ color: SUN }}>+50</div>
                <div className="text-xs text-[var(--srs-ink-dim)]">EuroBonus poeng</div>
              </div>
            </div>
            <div className="flex gap-6 pt-4 border-t border-dashed border-[var(--srs-line)]">
              <div>
                <div className="text-[9px] tracking-[0.25em] text-[var(--srs-ink-faint)]">PAYMENT</div>
                <div className="text-sm font-bold mt-1">Visa · Klarna</div>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.25em] text-[var(--srs-ink-faint)]">TRUMF</div>
                <div className="text-sm font-bold mt-1">1.5 %</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] tracking-[0.25em] text-[var(--srs-ink-faint)] mb-2">FLIGHT</div>
            <div className="font-display text-2xl font-bold leading-tight">EP<br />2026</div>
            <div className="mt-12 text-[9px] tracking-[0.25em] text-[var(--srs-ink-faint)]">GATE</div>
            <div className="font-display text-3xl font-bold" style={{ color: SUN }}>A1</div>
          </div>
        </div>
      </div>
      {/* Pill — Glistening Edge */}
      <div style={{ position: "absolute", bottom: -18, right: 16 }}>
        <div className="relative overflow-hidden inline-flex items-center gap-2 px-4 py-2.5" style={{ background: INK, color: "#fff", borderRadius: 999, fontSize: 12, fontWeight: 600, boxShadow: "0 12px 28px rgba(11,36,64,0.4)" }}>
          <span style={{ color: SUN, fontSize: 14 }}>✈</span>
          <span>Site detected</span>
          {/* The glint overlay */}
          <span style={{ position: "absolute", inset: 0, background: `linear-gradient(110deg, transparent 30%, ${SUN}55 50%, transparent 70%)`, animation: "europilotGlint 4s linear infinite", pointerEvents: "none" }} />
        </div>
      </div>
    </HeroFrame>
  );
}

// ===========================================================================
// DIRECTION 2 — "Flight Tracker Dashboard"
// SVG flight path connecting merchant → wallet, with status indicators.
// Pill = contrail sweep.
// ===========================================================================
function Direction2() {
  return (
    <HeroFrame label="Direction 2 — The Flight Tracker Dashboard">
      <div className="relative bg-[var(--srs-surface)] border border-[var(--srs-line)] p-8 rounded-md">
        <div className="text-[9px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-4">// LIVE · DETECTING BONUSES</div>
        {/* SVG flight path */}
        <svg viewBox="0 0 400 200" className="w-full h-44" style={{ overflow: "visible" }}>
          {/* dotted curve */}
          <path d="M 30,150 Q 200,20 370,150" fill="none" stroke="var(--srs-ink-faint)" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
          {/* solid colored progress */}
          <path d="M 30,150 Q 200,20 370,150" fill="none" stroke={SUN} strokeWidth="2" strokeDasharray="500" strokeDashoffset="500">
            <animate attributeName="stroke-dashoffset" from="500" to="0" dur="3s" fill="freeze" />
          </path>
          {/* merchant node (start) */}
          <circle cx="30" cy="150" r="6" fill={INK} />
          <text x="30" y="178" textAnchor="middle" fontSize="10" fill="var(--srs-ink-dim)" fontWeight="600">komplett.no</text>
          {/* wallet node (end) */}
          <circle cx="370" cy="150" r="8" fill={SUN} />
          <text x="370" y="178" textAnchor="middle" fontSize="10" fill="var(--srs-ink-dim)" fontWeight="600">+50 poeng</text>
          {/* plane at midpoint */}
          <text x="200" y="42" textAnchor="middle" fontSize="22" fill={SUN}>✈</text>
        </svg>
        {/* Status indicator chips */}
        <div className="grid grid-cols-3 gap-2 mt-5">
          {[
            { label: "Payment", value: "Visa", on: true },
            { label: "SAS", value: "+50 pts", on: true },
            { label: "Trumf", value: "1.5 %", on: true },
          ].map((s, i) => (
            <div key={i} className="px-3 py-2 border border-[var(--srs-line)] bg-[var(--srs-surface-2)] rounded-md">
              <div className="text-[8px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] uppercase flex items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.on ? "#10B981" : "var(--srs-line)", boxShadow: s.on ? "0 0 6px #10B981" : "none" }} />
                {s.label}
              </div>
              <div className="text-sm font-bold mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Pill — Contrail Sweep */}
      <div style={{ position: "absolute", bottom: -18, right: 16 }}>
        <div className="relative overflow-hidden inline-flex items-center gap-2 px-4 py-2.5" style={{ background: INK, color: "#fff", borderRadius: 999, fontSize: 12, fontWeight: 600, boxShadow: "0 12px 28px rgba(11,36,64,0.4)" }}>
          <span style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)", animation: "europilotContrail 5s ease-in-out infinite", transform: "translateX(-100%)", pointerEvents: "none" }} />
          <span style={{ position: "relative", zIndex: 1, color: SUN, fontSize: 14 }}>✈</span>
          <span style={{ position: "relative", zIndex: 1 }}>Bonus aktiv</span>
        </div>
      </div>
    </HeroFrame>
  );
}

// ===========================================================================
// DIRECTION 3 — "Brand Constellation Network"
// Abstract network: EuroPilot central node, merchant logos around, lines connecting.
// Pill = instrument-panel breathe (only the icon opacity-pulses).
// ===========================================================================
function Direction3() {
  const merchants = [
    { x: 30,  y: 25,  name: "Vipps" },
    { x: 78,  y: 18,  name: "Klarna" },
    { x: 90,  y: 65,  name: "SAS" },
    { x: 65,  y: 88,  name: "Trumf" },
    { x: 18,  y: 78,  name: "Visa" },
    { x: 8,   y: 48,  name: "Power" },
  ];
  return (
    <HeroFrame label="Direction 3 — The Brand Constellation Network">
      <div className="relative bg-[var(--srs-surface)] border border-[var(--srs-line)] rounded-md overflow-hidden" style={{ minHeight: 360 }}>
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {merchants.map((m, i) => (
            <line key={i} x1="50" y1="50" x2={m.x} y2={m.y} stroke="var(--srs-ink-faint)" strokeWidth="0.2" opacity="0.5" />
          ))}
          {/* one path lit up coral, animated to cycle */}
          <line x1="50" y1="50" x2={merchants[0].x} y2={merchants[0].y} stroke={SUN} strokeWidth="0.5" opacity="0.8">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
          </line>
        </svg>
        {/* Merchant nodes */}
        {merchants.map((m, i) => (
          <div key={i} style={{ position: "absolute", left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%, -50%)" }}>
            <div className="px-2.5 py-1 text-[9px] tracking-wider font-bold uppercase bg-[var(--srs-bg)] border border-[var(--srs-line)] rounded-full text-[var(--srs-ink-dim)]">
              {m.name}
            </div>
          </div>
        ))}
        {/* Central EuroPilot node */}
        <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-display font-bold text-sm" style={{ background: INK, boxShadow: `0 0 0 8px rgba(255,138,92,0.15), 0 0 24px rgba(255,138,92,0.35)` }}>
            <div className="text-center leading-tight">
              <div style={{ color: SUN, fontSize: 20 }}>✈</div>
              <div className="text-[9px] tracking-[0.2em] mt-0.5">EUROPILOT</div>
            </div>
          </div>
        </div>
      </div>
      {/* Pill — Instrument Panel Breathe (only the icon pulses opacity) */}
      <div style={{ position: "absolute", bottom: -18, right: 16 }}>
        <div className="inline-flex items-center gap-2 px-4 py-2.5" style={{ background: INK, color: "#fff", borderRadius: 999, fontSize: 12, fontWeight: 600, boxShadow: "0 12px 28px rgba(11,36,64,0.4)" }}>
          <span style={{ color: SUN, fontSize: 14, animation: "europilotBreathe 3.6s ease-in-out infinite" }}>✈</span>
          <span>Auto-collecting</span>
        </div>
      </div>
    </HeroFrame>
  );
}

// ===========================================================================
// Comparison page
// ===========================================================================
export default function HeroVariants() {
  return (
    <div className="srs min-h-screen bg-[var(--srs-bg)] text-[var(--srs-ink)]">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-[10px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)]">// DESIGN REVIEW</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-2">
          Hero variants — pick a direction
        </h1>
        <p className="text-[var(--srs-ink-dim)] mt-3 max-w-2xl">
          Three distinct hero compositions + matching pill animations. Each one rejects the wobbling/rotation feedback differently:
          glint sweep, contrail sweep, icon-only breathe.
        </p>
      </div>
      <Direction1 />
      <Direction2 />
      <Direction3 />
      <style>{`
        @keyframes europilotGlint   { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes europilotContrail{ 0% { transform: translateX(-100%); } 60% { transform: translateX(100%); } 100% { transform: translateX(100%); } }
        @keyframes europilotBreathe { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
        @media (prefers-reduced-motion: reduce) {
          [style*="europilotGlint"], [style*="europilotContrail"], [style*="europilotBreathe"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
